import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaBook, FaVolumeUp, FaPlay, FaPause, FaArrowRight, FaCheck, FaStar, FaGraduationCap, FaLightbulb, FaUsers } from 'react-icons/fa';
import SEO from '../components/SEO';

const PersianAlphabetGuide: React.FC = () => {
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Persian alphabet data
  const alphabetData = [
    { letter: 'ا', name: 'Alef', pronunciation: 'ā', english: 'A', example: 'آب (āb) - water' },
    { letter: 'ب', name: 'Be', pronunciation: 'b', english: 'B', example: 'بابا (bābā) - father' },
    { letter: 'پ', name: 'Pe', pronunciation: 'p', english: 'P', example: 'پدر (pedar) - father' },
    { letter: 'ت', name: 'Te', pronunciation: 't', english: 'T', example: 'توت (tut) - mulberry' },
    { letter: 'ث', name: 'Se', pronunciation: 's', english: 'S', example: 'ثواب (savāb) - reward' },
    { letter: 'ج', name: 'Je', pronunciation: 'j', english: 'J', example: 'جنگ (jang) - war' },
    { letter: 'چ', name: 'Che', pronunciation: 'ch', english: 'CH', example: 'چای (chāy) - tea' },
    { letter: 'ح', name: 'He', pronunciation: 'h', english: 'H', example: 'حرف (harf) - word' },
    { letter: 'خ', name: 'Khe', pronunciation: 'kh', english: 'KH', example: 'خوب (khub) - good' },
    { letter: 'د', name: 'Dal', pronunciation: 'd', english: 'D', example: 'دوست (dust) - friend' },
    { letter: 'ذ', name: 'Zal', pronunciation: 'z', english: 'Z', example: 'ذکر (zekr) - mention' },
    { letter: 'ر', name: 'Re', pronunciation: 'r', english: 'R', example: 'رود (rud) - river' },
    { letter: 'ز', name: 'Ze', pronunciation: 'z', english: 'Z', example: 'زبان (zabān) - language' },
    { letter: 'ژ', name: 'Zhe', pronunciation: 'zh', english: 'ZH', example: 'ژاله (zhāle) - dew' },
    { letter: 'س', name: 'Sin', pronunciation: 's', english: 'S', example: 'سلام (salām) - hello' },
    { letter: 'ش', name: 'Shin', pronunciation: 'sh', english: 'SH', example: 'شب (shab) - night' },
    { letter: 'ص', name: 'Sad', pronunciation: 's', english: 'S', example: 'صبح (sobh) - morning' },
    { letter: 'ض', name: 'Zad', pronunciation: 'z', english: 'Z', example: 'ضرب (zarb) - multiplication' },
    { letter: 'ط', name: 'Ta', pronunciation: 't', english: 'T', example: 'طبیعت (tabi\'at) - nature' },
    { letter: 'ظ', name: 'Za', pronunciation: 'z', english: 'Z', example: 'ظرف (zarf) - container' },
    { letter: 'ع', name: 'Ayn', pronunciation: 'a', english: 'A', example: 'عشق (eshq) - love' },
    { letter: 'غ', name: 'Ghayn', pronunciation: 'gh', english: 'GH', example: 'غذا (ghazā) - food' },
    { letter: 'ف', name: 'Fe', pronunciation: 'f', english: 'F', example: 'فردا (fardā) - tomorrow' },
    { letter: 'ق', name: 'Qaf', pronunciation: 'q', english: 'Q', example: 'قلم (qalam) - pen' },
    { letter: 'ک', name: 'Kaf', pronunciation: 'k', english: 'K', example: 'کتاب (ketāb) - book' },
    { letter: 'گ', name: 'Gaf', pronunciation: 'g', english: 'G', example: 'گل (gol) - flower' },
    { letter: 'ل', name: 'Lam', pronunciation: 'l', english: 'L', example: 'لطفاً (lotfan) - please' },
    { letter: 'م', name: 'Mim', pronunciation: 'm', english: 'M', example: 'مادر (mādar) - mother' },
    { letter: 'ن', name: 'Nun', pronunciation: 'n', english: 'N', example: 'نور (nur) - light' },
    { letter: 'و', name: 'Vav', pronunciation: 'v/w', english: 'V/W', example: 'وان (vān) - van' },
    { letter: 'ه', name: 'He', pronunciation: 'h', english: 'H', example: 'خانه (khāne) - house' },
    { letter: 'ی', name: 'Ye', pronunciation: 'y/i', english: 'Y/I', example: 'یاد (yād) - memory' }
  ];

  const handlePlayAudio = (letter: string) => {
    setIsPlaying(true);
    // Simulate audio playback
    setTimeout(() => setIsPlaying(false), 2000);
  };

  return (
    <>
      <SEO 
        title="Persian Alphabet Guide: Learn Farsi Letters & Pronunciation | پیروز Pirooz"
        description="Master the Persian alphabet with our comprehensive guide. Learn all 32 Farsi letters, pronunciation, and examples. Interactive alphabet chart with audio pronunciation for beginners."
        keywords={[
          'persian alphabet', 'farsi alphabet', 'persian letters', 'farsi letters',
          'persian alphabet chart', 'farsi alphabet chart', 'learn persian alphabet',
          'learn farsi alphabet', 'persian alphabet pronunciation', 'farsi alphabet pronunciation',
          'persian script', 'farsi script', 'persian writing system', 'farsi writing system',
          'persian alphabet for beginners', 'farsi alphabet for beginners', 'persian alphabet tutorial',
          'farsi alphabet tutorial', 'persian alphabet audio', 'farsi alphabet audio',
          'persian alphabet song', 'farsi alphabet song', 'persian alphabet practice',
          'farsi alphabet practice', 'persian alphabet exercises', 'farsi alphabet exercises'
        ]}
        canonicalUrl="/persian-alphabet-guide"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Persian Alphabet Learning Course",
          "description": "Complete guide to learning the Persian alphabet with pronunciation, examples, and interactive exercises.",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "پیروز Pirooz"
          },
          "educationalLevel": "Beginner",
          "inLanguage": ["en", "fa"],
          "courseCode": "PERSIAN-ALPHABET-101",
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "instructor": {
              "@type": "Person",
              "name": "پیروز Pirooz Team"
            }
          }
        }}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mb-6">
              <FaBook className="text-white text-3xl" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-6">
              Persian Alphabet Guide
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Master the Persian alphabet with our comprehensive guide. Learn all 32 letters, 
              pronunciation, and examples with interactive audio.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">32</div>
              <div className="text-gray-600">Letters</div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">4</div>
              <div className="text-gray-600">Forms per Letter</div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">Right</div>
              <div className="text-gray-600">Writing Direction</div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">Easy</div>
              <div className="text-gray-600">Learning Curve</div>
            </div>
          </div>

          {/* Alphabet Chart */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Persian Alphabet Chart</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
              {alphabetData.map((item, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105 ${
                    selectedLetter === item.letter
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => setSelectedLetter(selectedLetter === item.letter ? null : item.letter)}
                >
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Noto Naskh Arabic, serif' }}>
                      {item.letter}
                    </div>
                    <div className="text-sm font-semibold text-gray-600 mb-1">{item.name}</div>
                    <div className="text-xs text-gray-500 mb-2">{item.pronunciation}</div>
                    <div className="text-xs text-gray-400">{item.english}</div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayAudio(item.letter);
                      }}
                      className="mt-2 p-1 bg-green-100 hover:bg-green-200 rounded-full transition-colors"
                    >
                      {isPlaying ? <FaPause className="text-green-600 text-xs" /> : <FaPlay className="text-green-600 text-xs" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Letter Details */}
          {selectedLetter && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mb-12 border border-blue-200">
              {(() => {
                const letter = alphabetData.find(item => item.letter === selectedLetter);
                return letter ? (
                  <div className="text-center">
                    <div className="text-6xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Noto Naskh Arabic, serif' }}>
                      {letter.letter}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">{letter.name}</h3>
                    <div className="text-lg text-gray-600 mb-4">
                      Pronunciation: <span className="font-semibold text-blue-600">{letter.pronunciation}</span>
                    </div>
                    <div className="text-lg text-gray-600 mb-4">
                      English equivalent: <span className="font-semibold text-green-600">{letter.english}</span>
                    </div>
                    <div className="text-lg text-gray-700 bg-white rounded-lg p-4 shadow-sm">
                      Example: <span className="font-semibold text-purple-600">{letter.example}</span>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* Learning Tips */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <FaLightbulb className="text-yellow-600 mr-3" />
                Learning Tips
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Start with Basic Letters</h4>
                    <p className="text-gray-600 text-sm">Begin with ا, ب, پ, ت, ج, چ - these are the most common letters.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Practice Writing</h4>
                    <p className="text-gray-600 text-sm">Write each letter multiple times to build muscle memory.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Learn Letter Forms</h4>
                    <p className="text-gray-600 text-sm">Each letter has 4 forms: isolated, initial, medial, and final.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Use Audio Resources</h4>
                    <p className="text-gray-600 text-sm">Listen to native pronunciation to master the sounds.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <FaGraduationCap className="text-blue-600 mr-3" />
                Practice Exercises
              </h3>
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Letter Recognition</h4>
                  <p className="text-blue-700 text-sm">Practice identifying letters in different contexts and fonts.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">Writing Practice</h4>
                  <p className="text-green-700 text-sm">Trace letters and write them independently.</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-2">Pronunciation</h4>
                  <p className="text-purple-700 text-sm">Repeat after audio examples to perfect your pronunciation.</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-800 mb-2">Word Building</h4>
                  <p className="text-orange-700 text-sm">Combine letters to form simple Persian words.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Related Learning */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Continue Your Learning Journey</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link 
                to="/learn-farsi-fast" 
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-6 hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-between mb-4">
                  <FaStar className="text-2xl" />
                  <FaArrowRight />
                </div>
                <h4 className="text-xl font-bold mb-2">Learn Farsi Fast</h4>
                <p className="text-blue-100">Accelerated learning program for rapid progress</p>
              </Link>
              <Link 
                to="/flashcards" 
                className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-6 hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-between mb-4">
                  <FaBook className="text-2xl" />
                  <FaArrowRight />
                </div>
                <h4 className="text-xl font-bold mb-2">Vocabulary Flashcards</h4>
                <p className="text-green-100">Interactive flashcards for vocabulary building</p>
              </Link>
              <Link 
                to="/pathways" 
                className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl p-6 hover:from-purple-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-between mb-4">
                  <FaUsers className="text-2xl" />
                  <FaArrowRight />
                </div>
                <h4 className="text-xl font-bold mb-2">Learning Pathways</h4>
                <p className="text-purple-100">Structured courses for different skill levels</p>
              </Link>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Master Persian?</h2>
            <p className="text-xl mb-6 opacity-90">
              Join our comprehensive Persian learning program and start your journey to fluency
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/learn-farsi-fast" 
                className="bg-white text-green-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
              >
                Start Learning Now
              </Link>
              <Link 
                to="/pathways" 
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-green-600 transition-colors"
              >
                Explore Courses
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PersianAlphabetGuide;
